import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Link2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { safeHref } from "@/lib/utils";
import { Loading, EmptyState, Badge, Select, PageHeader, Panel } from "@/components/common";

const CLASS = ["fact", "observation", "inference"];
const VERIF = ["confirmed", "uncertain", "disputed", "outdated"];

export default function Research() {
  const { workspaceId } = useAuth();
  const [items, setItems] = useState(null);
  const [entities, setEntities] = useState({});
  const [f, setF] = useState({ classification: "", verification_status: "" });

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/evidence?workspace_id=${workspaceId}`);
    setItems(data);
    const ids = [...new Set(data.map((e) => e.entity_id).filter(Boolean))];
    if (ids.length) {
      const res = await api.get(`/entities?workspace_id=${workspaceId}&limit=500`);
      const map = {};
      res.data.items.forEach((e) => (map[e.id] = e.name));
      setEntities(map);
    }
  }, [workspaceId]);

  useEffect(() => { setItems(null); load(); }, [load]);

  const filtered = (items || []).filter((e) =>
    (!f.classification || e.classification === f.classification) &&
    (!f.verification_status || e.verification_status === f.verification_status));

  const conflicting = (items || []).filter((e) => e.verification_status === "disputed");

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <PageHeader title="Research & Evidence" sub="Sources, observations, verification, and conflicting information across the workspace." />

      {conflicting.length > 0 && (
        <div className="mb-6 p-4" style={{ border: "1px solid var(--si-danger)", borderRadius: 2, background: "var(--si-surface)" }}>
          <div className="si-label mb-1" style={{ color: "var(--si-danger)" }}>{conflicting.length} conflicting observation(s) preserved for review</div>
          <div className="text-sm" style={{ color: "var(--si-text-2)" }}>Disputed evidence is never silently overwritten — resolve it from the entity file or the Review Queue.</div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <Select value={f.classification} onChange={(e) => setF((p) => ({ ...p, classification: e.target.value }))} style={{ width: "auto" }}><option value="">All classifications</option>{CLASS.map((c) => <option key={c} value={c}>{c}</option>)}</Select>
        <Select value={f.verification_status} onChange={(e) => setF((p) => ({ ...p, verification_status: e.target.value }))} style={{ width: "auto" }}><option value="">All verification</option>{VERIF.map((c) => <option key={c} value={c}>{c}</option>)}</Select>
      </div>

      {!items ? <Loading /> : filtered.length === 0 ? (
        <EmptyState icon={FlaskConical} title="No evidence yet" hint="Attach sources and observations from any entity file." />
      ) : (
        <Panel>
          {filtered.map((ev) => (
            <div key={ev.id} className="px-5 py-3.5" style={{ borderBottom: "1px solid var(--si-border)" }}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap"><Badge value={ev.classification} /><Badge value={ev.verification_status} /><span className="si-label">{ev.source_kind}</span></div>
                {ev.entity_id && entities[ev.entity_id] && <Link to={`/app/entities/${ev.entity_id}`} className="text-sm flex items-center gap-1" style={{ color: "var(--si-silver)" }}>{entities[ev.entity_id]} <ExternalLink size={12} /></Link>}
              </div>
              {ev.excerpt && <div className="text-sm mt-1.5" style={{ color: "var(--si-text)" }}>{ev.excerpt}</div>}
              {ev.source_url && safeHref(ev.source_url) && <a href={safeHref(ev.source_url)} target="_blank" rel="noreferrer" className="text-xs font-mono flex items-center gap-1 mt-1" style={{ color: "var(--si-silver)" }}><Link2 size={11} /> {ev.source_url}</a>}
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}
